import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Copy, Check, PencilSimple, Lock } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LIME = '#c8f135';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Public Blog opt-in card for the Settings page.
 * Lets the founder enable a free /brands/<slug>/ page, claim their slug,
 * and (once-only) rename it.
 */
const PublicBlogSection = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [slugInput, setSlugInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/public-blog/settings`);
      setSettings(data);
    } catch {
      setSettings({ enabled: false, slug: null, slug_locked: false, public_url: null });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/public-blog/toggle`, {
        enabled: !settings.enabled,
      });
      setSettings(data);
      toast.success(data.enabled ? 'Public blog live at scalitos.com/brands/' + data.slug : 'Public blog turned off.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update public blog');
    } finally { setBusy(false); }
  };

  const rename = async () => {
    if (!slugInput.trim()) { toast.error('Pick a slug first.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/public-blog/rename-slug`, { slug: slugInput.trim() });
      setSettings(data);
      setRenaming(false);
      setSlugInput('');
      toast.success(`Slug locked to ${data.slug}. You cannot rename again.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    } finally { setBusy(false); }
  };

  const copy = () => {
    if (!settings?.public_url) return;
    navigator.clipboard.writeText(settings.public_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  if (loading) {
    return (
      <div id="public-blog" data-testid="public-blog-section"
        className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
        <div className="h-5 w-32 bg-white/5 rounded animate-pulse mb-3" />
        <div className="h-3 w-64 bg-white/5 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div id="public-blog" data-testid="public-blog-section"
      className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(200,241,53,0.08)', border: `1px solid ${LIME}33` }}>
          <Globe size={20} style={{ color: LIME }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-medium" style={{ color: '#f5f0e8' }}>Public Blog</h3>
            {settings.enabled && (
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                style={{ background: 'rgba(123,224,173,0.1)', color: '#7be0ad', border: '1px solid rgba(123,224,173,0.32)' }}
                data-testid="public-blog-live-badge">
                Live
              </span>
            )}
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.55)' }}>
            Get a free blog page at scalitos.com/brands/yourbrand — indexed by Google
            and discoverable in search. Posts you publish appear publicly here.
          </p>
        </div>

        {/* Toggle */}
        <button onClick={toggle} disabled={busy} data-testid="public-blog-toggle"
          aria-pressed={settings.enabled}
          style={{
            width: 44, height: 24, borderRadius: 999, padding: 2,
            background: settings.enabled ? LIME : 'rgba(255,255,255,0.1)',
            border: 'none', cursor: busy ? 'wait' : 'pointer', transition: 'background 0.2s',
            flexShrink: 0,
          }}>
          <span style={{
            display: 'block', width: 20, height: 20, borderRadius: '50%',
            background: settings.enabled ? '#04050a' : 'rgba(255,255,255,0.4)',
            transform: settings.enabled ? 'translateX(20px)' : 'translateX(0)',
            transition: 'transform 0.2s',
          }} />
        </button>
      </div>

      {settings.enabled && settings.slug && (
        <div className="pl-13 mt-4 pt-4 border-t border-white/[0.05]">
          <p style={{ fontFamily: MONO, fontSize: 9, color: 'rgba(245,240,232,0.4)', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
            Your public blog
          </p>

          {!renaming ? (
            <div className="flex items-center gap-2 flex-wrap">
              <code data-testid="public-blog-url"
                style={{ fontFamily: MONO, fontSize: 13, color: '#f5f0e8',
                  background: 'rgba(255,255,255,0.03)', padding: '8px 12px',
                  borderRadius: 8, border: '1px solid rgba(255,255,255,0.07)' }}>
                {settings.public_url}
              </code>
              <button onClick={copy} data-testid="public-blog-copy-btn"
                style={{ background: 'transparent', color: LIME, border: `1px solid ${LIME}55`,
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                {copied ? <><Check size={11} weight="bold" /> Copied</> : <><Copy size={11} weight="bold" /> Copy</>}
              </button>
              <a href={settings.public_url} target="_blank" rel="noopener noreferrer"
                data-testid="public-blog-view-btn"
                style={{ background: 'transparent', color: '#f5f0e8',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '8px 12px', borderRadius: 8,
                  fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  textDecoration: 'none',
                }}>
                View →
              </a>
              {settings.slug_locked ? (
                <span data-testid="public-blog-slug-locked"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontFamily: MONO, fontSize: 10, color: 'rgba(245,240,232,0.35)',
                    letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700,
                  }}>
                  <Lock size={10} weight="bold" /> Locked
                </span>
              ) : (
                <button onClick={() => { setSlugInput(settings.slug); setRenaming(true); }}
                  data-testid="public-blog-rename-btn"
                  style={{ background: 'transparent', color: 'rgba(245,240,232,0.55)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                    fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}>
                  <PencilSimple size={11} weight="bold" /> Rename once
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span style={{ fontFamily: MONO, fontSize: 13, color: 'rgba(245,240,232,0.55)' }}>
                scalitos.com/brands/
              </span>
              <input type="text" value={slugInput}
                onChange={(e) => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))}
                data-testid="public-blog-slug-input"
                autoFocus
                style={{ background: 'rgba(255,255,255,0.03)', color: '#f5f0e8',
                  border: `1px solid ${LIME}55`, padding: '8px 12px', borderRadius: 8,
                  fontFamily: MONO, fontSize: 13, outline: 'none', minWidth: 180 }}
                placeholder="your-brand" maxLength={40} />
              <button onClick={rename} disabled={busy} data-testid="public-blog-rename-save-btn"
                style={{ background: LIME, color: '#04050a', border: 'none', cursor: busy ? 'wait' : 'pointer',
                  padding: '8px 14px', borderRadius: 8,
                  fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                {busy ? 'Saving…' : 'Save · once only'}
              </button>
              <button onClick={() => { setRenaming(false); setSlugInput(''); }}
                style={{ background: 'transparent', color: 'rgba(245,240,232,0.55)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>Cancel</button>
              <p style={{ width: '100%', marginTop: 6, fontFamily: MONO, fontSize: 10.5,
                color: 'rgba(245,240,232,0.4)', letterSpacing: '0.04em' }}>
                Pick carefully. You can only rename your slug once.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Weekly Automation row */}
      {settings.enabled && (
        <AutomationToggle />
      )}
    </div>
  );
};

/* Weekly automation sub-component — shares the parent card */
const AutomationToggle = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public-blog/automation`)
      .then((r) => setEnabled(!!r.data.enabled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/public-blog/automation`, { enabled: !enabled });
      setEnabled(!enabled);
      toast.success(!enabled
        ? 'On. One new blog post will land in your Assets every week.'
        : 'Off. We won\u2019t auto-write anything.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setBusy(false); }
  };

  if (loading) return null;
  return (
    <div data-testid="weekly-automation-row"
      className="pl-13 mt-4 pt-4 border-t border-white/[0.05] flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <PencilSimple size={18} style={{ color: 'rgba(245,240,232,0.65)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium mb-1" style={{ color: '#f5f0e8' }}>Weekly automation</p>
        <p className="text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.55)' }}>
          ScalitOS writes one fresh blog post for you every week, in your voice. It lands in your Assets, ready to publish in a tap.
        </p>
      </div>
      <button onClick={toggle} disabled={busy} data-testid="weekly-automation-toggle"
        aria-pressed={enabled}
        style={{
          width: 44, height: 24, borderRadius: 999, padding: 2,
          background: enabled ? LIME : 'rgba(255,255,255,0.1)',
          border: 'none', cursor: busy ? 'wait' : 'pointer', transition: 'background 0.2s',
          flexShrink: 0,
        }}>
        <span style={{
          display: 'block', width: 20, height: 20, borderRadius: '50%',
          background: enabled ? '#04050a' : 'rgba(255,255,255,0.4)',
          transform: enabled ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 0.2s',
        }} />
      </button>
    </div>
  );
};

export default PublicBlogSection;
